import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Button, 
  IconButton, 
  TextField, 
  Box, 
  Typography, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Chip,
  Switch
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../../utils/IconHandler';
import SitesService from '../../../../services/SitesService';
import { getLocations } from '../../../../services/LocationService';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SiteProductLocationsModal from './SiteProductLocationsModal';
import { hasStoredPermission } from '../../../../utils/dashboardPermissions';

const ProductsTab = ({ categories, tags, onCategoriesChange, onTagsChange }) => {
  const { t } = useTranslation();
  const canManageSites = hasStoredPermission('MANAGE_SITES');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [webLocations, setWebLocations] = useState([]);
  const [locationStates, setLocationStates] = useState({});
  const [isLocationsModalOpen, setIsLocationsModalOpen] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [savingLocationId, setSavingLocationId] = useState(null);
  // Cuando solo hay una sucursal disponible, se muestra su switch directo en la tabla
  // (sin pasar por el modal "Disponibilidad por sucursal", que no tendría sentido con una sola opción).
  const [singleLocationStates, setSingleLocationStates] = useState({});
  const [savingSingleLocationProductId, setSavingSingleLocationProductId] = useState(null);
  
  // Filtros
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedSearchFilter, setDebouncedSearchFilter] = useState('');
  
  // Ref para manejar debounce
  const searchTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    productId: null,
    categoryId: null,
    name: '',
    description: '',
    shortDescription: '',
    featured: false,
    tagIds: []
  });

  // Cargar productos
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await SitesService.getSiteProducts(
        categoryFilter || null,
        tagFilter || null,
        debouncedSearchFilter || null
      );
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading products:', err);
      setError(err.response?.data?.message || 'Error al cargar productos');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, tagFilter, debouncedSearchFilter]);

  // Cargar productos disponibles
  const loadAvailableProducts = useCallback(async () => {
    try {
      const data = await SitesService.getAvailableProducts();
      setAvailableProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading available products:', err);
    }
  }, []);

  const loadWebLocations = useCallback(async () => {
    try {
      const [locationsData, draftConfig] = await Promise.all([
        getLocations(),
        SitesService.getDraftConfig()
      ]);

      const ecommerceLocationIds = Array.isArray(draftConfig?.siteConfig?.ecommerceLocationIds)
        ? draftConfig.siteConfig.ecommerceLocationIds.map(Number)
        : [];

      const filteredLocations = (Array.isArray(locationsData) ? locationsData : [])
        .filter((location) => !location.isPlant)
        .filter((location) => ecommerceLocationIds.includes(Number(location.id)));

      setWebLocations(filteredLocations);
    } catch (err) {
      console.error('Error loading ecommerce locations:', err);
    }
  }, []);

  // Debounce para el campo de búsqueda
  useEffect(() => {
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Crear nuevo timeout con debounce de 500ms
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchFilter(searchFilter);
    }, 500);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchFilter]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (isCreateModalOpen || isEditModalOpen) {
      loadAvailableProducts();
    }
  }, [isCreateModalOpen, isEditModalOpen, loadAvailableProducts]);

  useEffect(() => {
    loadWebLocations();
  }, [loadWebLocations]);

  // Con una sola sucursal disponible, se resuelve su estado por producto para poder
  // mostrar el switch directo en la tabla (ver columna 'locations' más abajo).
  useEffect(() => {
    if (webLocations.length !== 1 || products.length === 0) return;

    let cancelled = false;
    const locationId = Number(webLocations[0].id);

    Promise.all(
      products.map((product) =>
        SitesService.getSiteProductLocations(product.id, [locationId])
          .then((data) => {
            const match = (Array.isArray(data) ? data : []).find(
              (item) => Number(item.locationId) === locationId
            );
            const enabled = match ? match.enabled !== false : product.enabled !== false;
            return [product.id, enabled];
          })
          .catch(() => [product.id, product.enabled !== false])
      )
    ).then((entries) => {
      if (cancelled) return;
      setSingleLocationStates(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [webLocations, products]);

  const handleToggleSingleLocation = async (product, enabled) => {
    if (webLocations.length !== 1) return;
    const locationId = Number(webLocations[0].id);

    try {
      setSavingSingleLocationProductId(product.id);
      await SitesService.updateSiteProductLocation(product.id, locationId, enabled);
      setSingleLocationStates((prev) => ({ ...prev, [product.id]: enabled }));
    } catch (err) {
      console.error('Error updating product location visibility:', err);
      setError(err.response?.data?.message || 'Error al actualizar disponibilidad por sucursal');
    } finally {
      setSavingSingleLocationProductId(null);
    }
  };

  const handleCreate = () => {
    setFormData({
      productId: null,
      categoryId: null,
      name: '',
      description: '',
      shortDescription: '',
      featured: false,
      tagIds: []
    });
    setIsCreateModalOpen(true);
  };

  const handleEdit = async (product) => {
    try {
      setLoading(true);
      const fullProduct = await SitesService.getSiteProductById(product.id);
      setSelectedProduct(fullProduct);
      setFormData({
        productId: fullProduct.productId,
        categoryId: fullProduct.categoryId || null,
        name: fullProduct.name,
        description: fullProduct.description || '',
        shortDescription: fullProduct.shortDescription || '',
        featured: fullProduct.featured || false,
        tagIds: fullProduct.tags?.map(t => t.id) || []
      });
      setIsEditModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar producto');
    } finally {
      setLoading(false);
    }
  };

  const handleEnabledChange = async (product, newEnabled) => {
    try {
      await SitesService.updateSiteProduct(product.id, { enabled: newEnabled });
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, enabled: newEnabled } : p))
      );
    } catch (err) {
      console.error('Error updating product enabled:', err);
      setError(err.response?.data?.message || 'Error al actualizar disponibilidad');
    }
  };

  const handleOpenLocationsModal = async (product) => {
    try {
      setSelectedProduct(product);
      setLoadingLocations(true);
      setIsLocationsModalOpen(true);

      const locationIds = webLocations.map((location) => Number(location.id));
      const data = await SitesService.getSiteProductLocations(product.id, locationIds);
      const nextStates = {};

      (Array.isArray(data) ? data : []).forEach((item) => {
        nextStates[Number(item.locationId)] = item.enabled !== false;
      });

      locationIds.forEach((locationId) => {
        if (typeof nextStates[locationId] === 'undefined') {
          nextStates[locationId] = product.enabled !== false;
        }
      });

      setLocationStates(nextStates);
    } catch (err) {
      console.error('Error loading product location availability:', err);
      setError(err.response?.data?.message || 'Error al cargar disponibilidad por sucursal');
      setIsLocationsModalOpen(false);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleCloseLocationsModal = () => {
    setIsLocationsModalOpen(false);
    setSelectedProduct(null);
    setLocationStates({});
    setSavingLocationId(null);
  };

  const handleToggleLocation = async (locationId, enabled) => {
    if (!selectedProduct) return;

    try {
      setSavingLocationId(locationId);
      await SitesService.updateSiteProductLocation(selectedProduct.id, locationId, enabled);
      setLocationStates((prev) => ({
        ...prev,
        [locationId]: enabled
      }));
    } catch (err) {
      console.error('Error updating product location visibility:', err);
      setError(err.response?.data?.message || 'Error al actualizar disponibilidad por sucursal');
    } finally {
      setSavingLocationId(null);
    }
  };

  const handleDelete = (product) => {
    setSelectedProduct(product);
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = {
        productId: formData.productId,
        categoryId: formData.categoryId || null,
        name: formData.name,
        description: formData.description || null,
        shortDescription: formData.shortDescription || null,
        featured: formData.featured,
        tagIds: formData.tagIds
      };
      
      if (isCreateModalOpen) {
        await SitesService.createSiteProduct(data);
      } else {
        await SitesService.updateSiteProduct(selectedProduct.id, data);
      }
      
      setIsCreateModalOpen(false);
      setIsEditModalOpen(false);
      setSelectedProduct(null);
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar producto');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setError(null);
      await SitesService.deleteSiteProduct(selectedProduct.id);
      setIsDeleteDialogOpen(false);
      setSelectedProduct(null);
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar producto');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 80 
    },
    { 
      field: 'name', 
      headerName: t('name') || 'Nombre', 
      flex: 1 
    },
    {
      field: 'category',
      headerName: t('category') || 'Categoría',
      width: 150,
      renderCell: (params) => (
        params.row.category ? params.row.category.name : '-'
      )
    },
    {
      field: 'featured',
      headerName: t('featured') || 'Destacado',
      width: 120,
      renderCell: (params) => (
        params.row.featured ? t('yes') || 'Sí' : t('no') || 'No'
      )
    },
    ...(canManageSites ? [{
      field: 'enabled',
      headerName: 'En web (global)',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Switch
            checked={params.row.enabled !== false}
            onChange={(e) => handleEnabledChange(params.row, e.target.checked)}
            size="small"
            sx={{
              width: 40,
              height: 22,
              padding: 0,
              '& .MuiSwitch-switchBase': {
                padding: 0,
                top: 0,
                left: 1,
                '&.Mui-checked': {
                  transform: 'translateX(20px)',
                  color: '#fff',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#34C759',
                    opacity: 1,
                  },
                },
              },
              '& .MuiSwitch-thumb': {
                width: 18,
                height: 18,
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                marginBottom: '18px',
              },
              '& .MuiSwitch-track': {
                borderRadius: 11,
                backgroundColor: '#e9e9ea',
                opacity: 1,
              },
            }}
          />
        </Box>
      )
    }] : []),
    {
      field: 'locations',
      headerName: 'Sucursales',
      width: 140,
      sortable: false,
      renderCell: (params) => (
        webLocations.length === 1 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <Switch
              checked={singleLocationStates[params.row.id] !== false}
              disabled={savingSingleLocationProductId === params.row.id || !canManageSites}
              onChange={(e) => handleToggleSingleLocation(params.row, e.target.checked)}
              size="small"
              sx={{
                width: 40,
                height: 22,
                padding: 0,
                '& .MuiSwitch-switchBase': {
                  padding: 0,
                  top: 0,
                  left: 1,
                  '&.Mui-checked': {
                    transform: 'translateX(20px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                      backgroundColor: '#34C759',
                      opacity: 1,
                    },
                  },
                },
                '& .MuiSwitch-thumb': {
                  width: 18,
                  height: 18,
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  marginBottom: '18px',
                },
                '& .MuiSwitch-track': {
                  borderRadius: 11,
                  backgroundColor: '#e9e9ea',
                  opacity: 1,
                },
              }}
            />
          </Box>
        ) : (
          <Button
            size="small"
            startIcon={<StorefrontIcon fontSize="small" />}
            onClick={() => handleOpenLocationsModal(params.row)}
            disabled={webLocations.length === 0}
          >
            Configurar
          </Button>
        )
      )
    },
    {
      field: 'tags',
      headerName: t('tags') || 'Tags',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', height: '100%' }}>
          {params.row.tags?.map(tag => (
            <Chip key={tag.id} label={tag.name} size="small" />
          ))}
        </Box>
      )
    },
    ...(canManageSites ? [{
      field: 'actions',
      headerName: t('actions') || 'Acciones',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }] : [])
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">{t('products') || 'Productos'}</Typography>
        {canManageSites && (
          <Button
            variant="contained"
            startIcon={getIcon('add')}
            onClick={handleCreate}
            disabled={loading}
          >
            {t('create_product') || 'Crear Producto'}
          </Button>
        )}
      </Box>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label={t('search') || 'Buscar'}
          size="small"
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('category') || 'Categoría'}</InputLabel>
          <Select
            value={categoryFilter}
            label={t('category') || 'Categoría'}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">{t('all') || 'Todas'}</MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('tag') || 'Tag'}</InputLabel>
          <Select
            value={tagFilter}
            label={t('tag') || 'Tag'}
            onChange={(e) => setTagFilter(e.target.value)}
          >
            <MenuItem value="">{t('all') || 'Todos'}</MenuItem>
            {tags.map(tag => (
              <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DataGrid
        rows={products}
        columns={columns}
        getRowId={(row) => row.id}
        autoHeight
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } }
        }}
      />

      {/* Modal Crear/Editar */}
      <Dialog 
        open={isCreateModalOpen || isEditModalOpen} 
        onClose={() => {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setSelectedProduct(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isCreateModalOpen ? (t('create_product') || 'Crear Producto') : (t('edit_product') || 'Editar Producto')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              fullWidth
              options={availableProducts}
              getOptionLabel={(option) => option?.name ?? ''}
              value={
                availableProducts.find((product) => product.id === formData.productId)
                ?? (isEditModalOpen && formData.productId
                  ? { id: formData.productId, name: selectedProduct?.originalProductName || formData.name }
                  : null)
              }
              onChange={(_, newValue) => {
                setFormData({ 
                  ...formData, 
                  productId: newValue?.id ?? null,
                  name: newValue?.name ?? formData.name
                });
              }}
              disabled={isEditModalOpen}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('original_product') || 'Producto Original'}
                  required
                />
              )}
            />

            <TextField
              label={t('name') || 'Nombre'}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('category') || 'Categoría'}</InputLabel>
                <Select
                  value={formData.categoryId || ''}
                  label={t('category') || 'Categoría'}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value || null })}
                >
                  <MenuItem value="">{t('none') || 'Ninguna'}</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('tags') || 'Tags'}</InputLabel>
                <Select
                  multiple
                  value={formData.tagIds}
                  label={t('tags') || 'Tags'}
                  onChange={(e) => setFormData({ ...formData, tagIds: e.target.value })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const tag = tags.find(t => t.id === value);
                        return tag ? <Chip key={value} label={tag.name} size="small" /> : null;
                      })}
                    </Box>
                  )}
                >
                  {tags.map(tag => (
                    <MenuItem key={tag.id} value={tag.id}>
                      {tag.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              label={t('short_description') || 'Descripción Corta'}
              value={formData.shortDescription}
              onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />

            <TextField
              label={t('description') || 'Descripción'}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={4}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                />
              }
              label={t('featured') || 'Producto Destacado'}
            />

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setSelectedProduct(null);
          }}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading || !formData.productId || !formData.name}
          >
            {t('save') || 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Eliminar */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>{t('confirm_delete') || 'Confirmar Eliminación'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirm_delete_product', { name: selectedProduct?.name }) || `¿Estás seguro de que deseas eliminar el producto "${selectedProduct?.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={loading}>
            {t('delete') || 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <SiteProductLocationsModal
        open={isLocationsModalOpen}
        onClose={handleCloseLocationsModal}
        product={selectedProduct}
        locations={webLocations}
        locationStates={locationStates}
        loading={loadingLocations}
        savingLocationId={savingLocationId}
        onToggle={handleToggleLocation}
        canManageSites={canManageSites}
      />
    </Box>
  );
};

export default ProductsTab;
